// Fires TURN credential fetch at module evaluation time -- before React mounts
import './api/modules/iceServerPrefetch';
// Fires WebSocket + CONN_BEACON_FAST_START at module evaluation time for /j/ paths
import './api/modules/beaconConnectionPrefetch';

import { armWebrtcDebugLogging } from '@playcastdotio/PeerCore';

// DEBUG BUILD ONLY — arm the WebRTC debug-logging gate in dev servers. Without this the
// guest's clientStats/guestDebug datachannel logging (and the host's [GuestDebug.host]
// NDJSON sink) stays off in every `yarn serve-all:dev` session, because vite's `define`
// only substitutes PLAYCAST_DEBUG_WEBRTC in build output. Runs before any PeerCore
// manager module is imported or constructed.
armWebrtcDebugLogging(import.meta.env.DEV);

import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { App } from './app/app';
import constants from '@playcastdotio/GlobalConfig';
import { init, browserTracingIntegration } from '@sentry/react';
import config from './config.json';
import { HelmetProvider } from 'react-helmet-async';
import { getPersistentPlayerId } from './api';
import { authStore } from './api/stores/globalStores';

// Detect campaign subdomain
// Production: skate.demo.playcast.io (4 parts, slug = parts[0])
// Non-production: skate.dev.demo.playcast.io (5 parts, slug = parts[0])
const hostname = window.location.hostname;
const isLocalhost = hostname === 'localhost';
const hostParts = hostname.split('.');
const versionBranch = constants.VERSION.split('-')[1];
const isProductionBuild = !versionBranch || versionBranch === 'release';

const isCampaignSubdomain =
  !isLocalhost &&
  (
    (isProductionBuild && hostParts.length === 4 && hostParts[1] === 'demo' && hostParts[2] === 'playcast') ||
    (!isProductionBuild && hostParts.length === 5 && hostParts[2] === 'demo' && hostParts[3] === 'playcast')
  );

if (isCampaignSubdomain) {
  const campaignSlug = hostParts[0];
  const currentPath = window.location.pathname;
  if (currentPath === '/' || currentPath === '/index.html') {
    window.location.replace(`/q/${campaignSlug}${window.location.search}`);
    throw new Error(`Redirecting to /q/${campaignSlug}`);
  }
}

// Redirect pavilion domains to playcast.io (respects environment)
if (!isLocalhost && hostname.includes('pavilion')) {
  console.log('[Redirect] Pavilion domain detected, redirecting to playcast.io');

  const isLocal = constants.VERSION.endsWith('-local');
  const branch = constants.VERSION.split('-')[1]; // e.g., "1.15.0-dev" → "dev"

  let homeUrl = '';

  if (isLocal) {
    // Local: http://localhost:4204/
    homeUrl = 'http://localhost:4204/';
  } else {
    // Build URL based on branch
    switch (branch) {
      case 'dev':
        homeUrl = 'https://dev.marketing.playcast.io/';
        break;
      case 'dev2':
        homeUrl = 'https://dev2.marketing.playcast.io/';
        break;
      case 'prerelease':
        homeUrl = 'https://prerelease.marketing.playcast.io/';
        break;
      default:
        // Release: https://playcast.io/
        homeUrl = 'https://playcast.io/';
        break;
    }
  }

  console.log(`[Redirect] Redirecting to: ${homeUrl}`);
  window.location.href = homeUrl;
  // Stop execution to prevent rendering
  throw new Error('Redirecting to playcast.io');
}

// Redirect prerelease skateTest → production skate
// TODO: remove this crap once we are done, please.
if (
  hostname === 'prerelease.demo.playcast.io' &&
  window.location.pathname === '/q/skateTest'
) {
  window.location.href = 'https://demo.playcast.io/q/skate';
  throw new Error('Redirecting skateTest to production skate');
}

// Initialize Sentry immediately
if (!constants.VERSION.endsWith('-local')) {
  init({
    release: constants.VERSION,
    environment: constants.ENVIRONMENT,
    dsn: config.SENTRY_DSN,
    normalizeDepth: 10,
    integrations: [browserTracingIntegration()],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/.*\.playcast\.io(?!.*clerk)/,
      /^https:\/\/.*\.gibber\.com(?!.*clerk)/,
      /^https:\/\/.*\.api\.gibber\.com/,
    ],
  });
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

// Move playerId initialization to after render
setTimeout(() => {
  getPersistentPlayerId();
}, 0);

// No Clerk in the load path — mark auth as loaded so nothing waits on it
authStore.updateState({ ...authStore.currentState, loaded: true });

root.render(
  <StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </StrictMode>,
);
